import { useCallback, useEffect } from 'react';

import { useLazyGetMedicalDocumentByRecordIdQuery } from '@app/api/MedicalDocuments';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useAppSelector } from '@app/store';

import type { UseMedicalCardEffectsProps } from '../types';

export const useMedicalCardEffects = ({
  setInitialData,
  getClientInfo,
  getDoctorInfo,
}: UseMedicalCardEffectsProps) => {
  const { record } = useAppSelector(selectRecordModal);

  const { showNotification } = useNotification();
  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [
    getMedicalDocumentByRecordId,
    { isFetching: isGetMedicalDocumentLoading, isUninitialized },
  ] = useLazyGetMedicalDocumentByRecordIdQuery();

  const getInitialData = useCallback(async () => {
    try {
      if (record?.Id) {
        const result = await getMedicalDocumentByRecordId({
          recordId: record.Id,
          ActiveBranchId,
        }).unwrap();

        setInitialData(result);

        if (!result.length) {
          await getClientInfo();
          await getDoctorInfo();
        }
      }
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    record?.Id,
    getMedicalDocumentByRecordId,
    ActiveBranchId,
    setInitialData,
    getClientInfo,
    getDoctorInfo,
    showNotification,
  ]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const isEffectsLoading =
    isGetMedicalDocumentLoading || (isUninitialized && !!record?.Id);

  return { isEffectsLoading };
};
